import {
  PencilIcon,
  TablePropertiesIcon,
  Trash2Icon,
  XSquareIcon,
  ArrowLeftIcon
} from "lucide-react";
import { CopyIcon } from "lucide-react";
import { useEffect, useMemo, useReducer } from "react";
import { useSearchParams } from "react-router-dom";

import { deleteForm } from "@/api/form";
import { createForm } from "@/api/form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { MoonLoader } from "react-spinners";

import { useForms } from "@/hooks/server-state/use-forms";
import { useDashboardTabsStore } from "@/hooks/store/use-dashboard-tabs-store";
import { useFormModalStore } from "@/hooks/store/use-form-modal-store";

import { formatCategoryNames } from "@/utils/category";
import { extractCategories } from "@/utils/category";

import AlertModal from "@/components/common/modals/alert-modal";
import EntitiesListModal from "@/components/common/modals/entities-list-modal";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TablePagination,
  TableRow,
  TableSearch
} from "@/components/ui/table";

import type { CategoryType } from "@/types/category";
import type { FormType } from "@/types/form/form";

enum ActionTypes {
  SET_FORM = "SET_FORM",
  SET_IS_MODAL_OPEN = "SET_IS_MODAL_OPEN",
  SET_IS_DELETE_MODAL_OPEN = "SET_IS_DELETE_MODAL_OPEN",
  SET_IS_ENTITIES_LIST_MODAL_OPEN = "SET_IS_ENTITIES_LIST_MODAL_OPEN"
}

type Actions = {
  type: ActionTypes;
  payload?: any;
};

const initialState = {
  isDeleteModalOpen: false,
  isModalOpen: false,
  isEntitiesListModalOpen: false,
  form: null
};

const formReducer = (state: typeof initialState, action: Actions) => {
  switch (action.type) {
    case ActionTypes.SET_FORM:
      return { ...state, form: action.payload };
    case ActionTypes.SET_IS_MODAL_OPEN:
      return { ...state, isModalOpen: action.payload };
    case ActionTypes.SET_IS_DELETE_MODAL_OPEN:
      return { ...state, isDeleteModalOpen: action.payload };
    case ActionTypes.SET_IS_ENTITIES_LIST_MODAL_OPEN:
      return { ...state, isEntitiesListModalOpen: action.payload };

    default:
      return state;
  }
};

type CategorySummary = CategoryType & {
  formCount: number;
};

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = PAGE_SIZE_OPTIONS[0];

const getPageSize = (value: string | null) => {
  const requestedSize = Number(value);

  return PAGE_SIZE_OPTIONS.includes(requestedSize)
    ? requestedSize
    : DEFAULT_PAGE_SIZE;
};

const getCurrentPage = (value: string | null) => {
  const requestedPage = Number(value);

  return Number.isFinite(requestedPage) && requestedPage > 0
    ? requestedPage
    : 1;
};

const matchesFormSearch = (form: FormType, searchValue: string) => {
  const normalizedSearchValue = searchValue.trim().toLowerCase();

  if (!normalizedSearchValue) {
    return true;
  }

  return [form.name, form.description, form.htmlFormBody]
    .filter(Boolean)
    .some((value) => String(value).toLowerCase().includes(normalizedSearchValue));
};

const FormsPage = () => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: res, isLoading } = useForms({ page: 1, size:1000, search: "" });
  const { mutate, isPending } = useMutation({
    mutationFn: (formId: number) => deleteForm(formId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["forms"] })
  });
  const { onOpen, setForm } = useFormModalStore();
  const navigate = useNavigate();
  const { mutate: cloneForm } = useMutation({
    mutationFn: createForm,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["forms"] });
    }
  });
  const [form, dispatch] = useReducer(formReducer, initialState);
  const { openTab } = useDashboardTabsStore();

  const allForms = res?.data ?? [];
  const searchValue = searchParams.get("search") ?? "";
  const currentPage = getCurrentPage(searchParams.get("page"));
  const pageSize = getPageSize(searchParams.get("size"));
  const isAllFormsView = searchParams.get("view") === "all";

  const selectedCategoryIdValue = Number(searchParams.get("categoryId"));
  const selectedCategoryId =
    !isAllFormsView && Number.isFinite(selectedCategoryIdValue) && selectedCategoryIdValue > 0
      ? selectedCategoryIdValue
      : null;

  const categoryRows = useMemo<CategorySummary[]>(() => {
    const categoriesMap = new Map<number, CategorySummary>();

    allForms.forEach((formItem: FormType) => {
      extractCategories(formItem).forEach((category) => {
        const existingCategory = categoriesMap.get(category.id);

        if (existingCategory) {
          existingCategory.formCount += 1;
          return;
        }

        categoriesMap.set(category.id, {
          ...category,
          formCount: 1
        });
      });
    });

    return Array.from(categoriesMap.values()).sort((firstCategory, secondCategory) =>
      firstCategory.name.localeCompare(secondCategory.name, "fa")
    );
  }, [allForms]);

  const selectedCategory = useMemo(
    () =>
      selectedCategoryId
        ? categoryRows.find((category) => category.id === selectedCategoryId) ?? null
        : null,
    [categoryRows, selectedCategoryId]
  );

  const filteredForms = useMemo(() => {
    if (isAllFormsView) {
      return allForms.filter((formItem: FormType) => matchesFormSearch(formItem, searchValue));
    }

    if (!selectedCategory) {
      return [];
    }

    return allForms.filter((formItem: FormType) => {
      const categoryIds = extractCategories(formItem).map((category) => category.id);

      return categoryIds.includes(selectedCategory.id) && matchesFormSearch(formItem, searchValue);
    });
  }, [allForms, isAllFormsView, searchValue, selectedCategory]);

  const totalPages = Math.max(1, Math.ceil(filteredForms.length / pageSize));
  const startIndex = (currentPage - 1) * pageSize;
  const visibleForms = filteredForms.slice(startIndex, startIndex + pageSize);

  const handleClone = (formItem: any) => {
    const fd = new FormData();

    fd.append("Id", "0");
    fd.append("Name", `${formItem.name} (copy)`);
    fd.append("Description", formItem.description ?? "");
    fd.append("HtmlFormBody", formItem.htmlFormBody ?? "");
    fd.append("Type", String(formItem.type ?? 1));

    cloneForm(fd);
  };

  const handleCategorySelect = (categoryId: number) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("categoryId", categoryId.toString());
      params.delete("view");
      params.set("page", "1");
      params.delete("size");
      return params;
    });
  };

  const handleAllFormsSelect = () => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("view", "all");
      params.delete("categoryId");
      params.set("page", "1");
      params.delete("size");
      return params;
    });
  };

  const handleBackToCategories = () => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.delete("view");
      params.delete("categoryId");
      params.delete("page");
      params.delete("size");
      return params;
    });
  };

  useEffect(() => {
    if (!selectedCategory && !isAllFormsView) {
      return;
    }

    if (isAllFormsView) {
      return;
    }

    // Avoid resetting page from URL before data is ready.
    if (isLoading) {
      return;
    }

    if (currentPage > totalPages) {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.set("page", totalPages.toString());
        return params;
      });
    }
  }, [currentPage, isAllFormsView, isLoading, selectedCategory, setSearchParams, totalPages]);

  if (isLoading) return <Loading />;

  return (
    <>
      <AlertModal
        isOpen={form.isDeleteModalOpen}
        onClose={() =>
          dispatch({
            type: ActionTypes.SET_IS_DELETE_MODAL_OPEN,
            payload: false
          })
        }
        isLoading={isPending}
        onConfirm={async () => {
          mutate(form?.form?.id);
        }}
        title="آیا از حذف فرم اطمینان دارید؟"
        description="این عملیات قابل برگشت نخواهد بود و فرم بصورت دائمی حذف خواهد شد"
      />

      <EntitiesListModal
        isOpen={form.isEntitiesListModalOpen}
        formId={form.form?.id}
        onClose={() => {
          dispatch({
            type: ActionTypes.SET_IS_ENTITIES_LIST_MODAL_OPEN,
            payload: false
          });
        }}
      />

      {selectedCategory || isAllFormsView ? (
        <>
          <div className="flex items-start justify-between gap-3 px-5 py-2">
            <div className="flex flex-col gap-2">
              <button
                className="flex items-center gap-x-1 text-sm hover:text-primary"
                onClick={handleBackToCategories}
              >
                <ArrowLeftIcon size={14} className="text-primary" />
                بازگشت به دسته بندی ها
              </button>

              <div className="text-sm text-slate-600">
                {isAllFormsView ? (
                  <>همه فرم ها</>
                ) : (
                  <>
                    فرم‌های دسته بندی <span className="font-semibold text-slate-900">{selectedCategory?.name}</span>
                  </>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <TableSearch className="h-8 w-full sm:w-72" />
              <button
                className="flex items-center gap-x-1 text-sm hover:text-primary"
                onClick={() => {
                  onOpen();
                  setForm(null);
                }}
              >
                <XSquareIcon size={14} className="text-primary" />
                ساخت فرم جدید
              </button>
            </div>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ردیف</TableHead>
                <TableHead>نام</TableHead>
                <TableHead>توضیحات</TableHead>
                <TableHead>دسته بندی ها</TableHead>
                <TableHead>جداول</TableHead>
                <TableHead>ویرایش</TableHead>
                <TableHead>کلون</TableHead>
                <TableHead>حذف</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {visibleForms.map((formItem, index) => (
                <TableRow
                  key={formItem.id}
                  onClick={(e) => {
                    e.stopPropagation();

                    const path = "/dashboard/form/editor/" + formItem.id;

                    openTab({
                      path,
                      label: formItem.name
                    });

                    navigate(path);
                  }}
                  className="cursor-pointer"
                >
                  <TableCell>{startIndex + index + 1}</TableCell>
                  <TableCell>{formItem.name}</TableCell>
                  <TableCell>{formItem.description}</TableCell>
                  <TableCell className="max-w-xs whitespace-normal">
                    {formatCategoryNames(formItem) || "-"}
                  </TableCell>
                  <TableCell className="text-center">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        dispatch({
                          type: ActionTypes.SET_IS_ENTITIES_LIST_MODAL_OPEN,
                          payload: true
                        });
                        dispatch({
                          type: ActionTypes.SET_FORM,
                          payload: formItem
                        });
                      }}
                    >
                      <TablePropertiesIcon className="text-slate-700" />
                    </button>
                  </TableCell>
                  <TableCell className="text-center">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setForm(formItem);
                        onOpen();
                      }}
                    >
                      <PencilIcon className="text-slate-700" />
                    </button>
                  </TableCell>
                  <TableCell className="text-center">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClone(formItem);
                      }}
                      title="Clone"
                    >
                      <CopyIcon className="text-slate-700" />
                    </button>
                  </TableCell>
                  <TableCell className="text-center">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        dispatch({
                          type: ActionTypes.SET_IS_DELETE_MODAL_OPEN,
                          payload: true
                        });
                        dispatch({
                          type: ActionTypes.SET_FORM,
                          payload: formItem
                        });
                      }}
                    >
                      <Trash2Icon className="text-red-500" />
                    </button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {!visibleForms.length && <EmptyState message="فرمی برای این دسته بندی پیدا نشد" />}

          <TablePagination totalItems={filteredForms.length} />
        </>
      ) : (
        <>
          <div className="flex items-center justify-between px-5 py-2">
            <div className="flex flex-col gap-1">
              <p className="text-sm font-medium text-slate-900">دسته بندی فرم ها</p>
              <p className="mt-1 text-xs text-slate-500">یک دسته بندی را انتخاب کنید تا فرم های مربوط به آن نمایش داده شود.</p>
            </div>

            <button
              className="flex items-center gap-x-1 text-sm hover:text-primary"
              onClick={() => {
                onOpen();
                setForm(null);
              }}
            >
              <XSquareIcon size={14} className="text-primary" />
              ساخت فرم جدید
            </button>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ردیف</TableHead>
                <TableHead>نام دسته بندی</TableHead>
                <TableHead>تعداد فرم</TableHead>
                <TableHead>توضیحات</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              <TableRow className="cursor-pointer" onClick={handleAllFormsSelect}>
                <TableCell>1</TableCell>
                <TableCell className="font-medium text-primary">همه</TableCell>
                <TableCell>{allForms.length}</TableCell>
                <TableCell>نمایش همه فرم ها</TableCell>
              </TableRow>

              {categoryRows.map((categoryItem, index) => (
                <TableRow
                  key={categoryItem.id}
                  className="cursor-pointer"
                  onClick={() => handleCategorySelect(categoryItem.id)}
                >
                  <TableCell>{index + 2}</TableCell>
                  <TableCell>{categoryItem.name}</TableCell>
                  <TableCell>{categoryItem.formCount}</TableCell>
                  <TableCell>{categoryItem.description || "-"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {!categoryRows.length && <EmptyState message="دسته بندی پیدا نشد" />}
        </>
      )}
    </>
  );
};

const Loading = () => (
  <div className="flex h-[calc(100vh/2)] w-full items-center justify-center">
    <MoonLoader color="#0099A5" size={50} />
  </div>
);

const EmptyState = ({ message = "فرمی پیدا نشد" }: { message?: string }) => (
  <div className="flex h-32 w-full items-center justify-center bg-white shadow-md">
    <p className="text-slate-500">{message}</p>
  </div>
);

export default FormsPage;
